import { assetUrl } from '../api/client'
import type { AuditEvent } from '../api/types'
import { formatDateTime, humanise } from '../lib/format'
import { StatusChip } from './StatusChip'

/**
 * One completed delivery run's evidence for this invoice, read off the audit
 * trail's `delivery_completed` event: who received it, what happened to each
 * item, the signature, the handover photos and where the driver was.
 */
export function ProofOfDelivery({ event }: { event: AuditEvent }) {
  const lat = event.delivery_latitude
  const lng = event.delivery_longitude
  const hasFix = lat != null && lng != null

  return (
    <div className="stack">
      <dl className="dl">
        <dt>Completed</dt>
        <dd>{formatDateTime(event.timestamp)}</dd>
        <dt>Driver</dt>
        <dd>{event.actor ?? '—'}</dd>
        <dt>Received by</dt>
        <dd>{event.recipient_name || '—'}</dd>
        <dt>Location</dt>
        <dd>
          {hasFix ? (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
              target="_blank"
              rel="noreferrer"
            >
              {lat.toFixed(5)}, {lng.toFixed(5)} ↗
            </a>
          ) : (
            'No GPS fix recorded'
          )}
        </dd>
      </dl>

      {event.items?.length ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th className="num">Qty</th>
                <th>Outcome</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {event.items.map((item, index) => (
                <tr key={`${item.name}-${index}`}>
                  <td>
                    {item.name}
                    {item.model ? <div className="cell-sub">{item.model}</div> : null}
                  </td>
                  <td className="num">
                    ×{item.qty}
                    {item.line_qty ? <span className="cell-sub"> of {item.line_qty}</span> : null}
                  </td>
                  <td>
                    <StatusChip status={item.outcome} />
                  </td>
                  <td className="cell-sub">
                    {item.reason ? humanise(item.reason) : '—'}
                    {item.reason_notes ? <div>{item.reason_notes}</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {event.signature_url || event.photo_urls.length ? (
        <div className="pod-media">
          {event.signature_url ? (
            <a
              className="signature"
              href={assetUrl(event.signature_url)}
              target="_blank"
              rel="noreferrer"
            >
              <img src={assetUrl(event.signature_url)} alt="Customer signature" loading="lazy" />
              <div className="caption">Signature</div>
            </a>
          ) : null}
          {event.photo_urls.map((url, index) => (
            <a key={url} href={assetUrl(url)} target="_blank" rel="noreferrer">
              <img src={assetUrl(url)} alt={`Handover photo ${index + 1}`} loading="lazy" />
              <div className="caption">Photo {index + 1}</div>
            </a>
          ))}
        </div>
      ) : (
        <p className="hint">No signature or photos were captured on this run.</p>
      )}
    </div>
  )
}
